import { CommonModule, DatePipe } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { RouterModule } from '@angular/router';
import { AbilityModule } from '@casl/angular';
import {
  DateDurationFormatPipe,
  DateFormatPipe,
  DurationFormatPipe,
  FilterPipe,
  HumanReadablePipe,
  HumanReadableSizePipe,
  SafePipe,
  SentenceCasePipe,
} from 'src/app/shared/pipes';
import { MaterialModule } from '../material/material.module';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';

import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { AppIconComponent } from './components/app-icon/app-icon.component';
import { CarouselComponent } from './components/carousel/carousel.component';
import {
  ComponentDirective,
  ConfirmDialogComponent,
  EsriMapComponent,
  FileUploaderComponent,
  GoogleMapsComponent,
  SearchComponent,
  SidebarComponent,
} from './components';
import { EquipmentTypesComponent } from './components/equipment-components/equipment-types/equipment-types.component';
import { FieldInputComponent } from './components/fields/field-input/field-input.component';
import { FieldsComponent } from './components/fields/fields.component';
import { JobCodesComponent } from './components/labor-components/job-codes/job-codes.component';
import { LoadingScreenComponent } from './components/loading-screen/loading-screen.component';
import { LocationPickerComponent } from './components/location-picker/location-picker.component';
import { DesignContextComponent } from './components/design-context/design-context.component';
import { DesignContextChipsComponent } from './components/design-context-chips/design-context-chips.component';
import { DesignCapturePreviewComponent } from './components/design-capture-preview/design-capture-preview.component';
import { DesignContextPickerDialogComponent } from './components/design-context-picker/design-context-picker-dialog.component';
import { DesignContextSheetPickerComponent } from './components/design-context-sheet-picker/design-context-sheet-picker.component';
import { DESIGN_CONTEXT_PICKER_DIALOG } from './components/design-context-picker/design-context-picker.token';
import { MaterialTestsComponent } from './components/material-tests/material-tests.component';
import { ModalComponent, ModalHostDirective } from './components/modal/modal.component';
import { ProgressCardComponent } from './components/progress-card/progress-card.component';
import { ProjectCardComponent } from './components/project/project-card/project-card.component';
import { ReportsComponent } from './components/reports/reports.component';
import { SubmittalPackageLogComponent } from './components/submittal-package-log/submittal-package-log.component';
import { TranslationsComponent } from './components/translations/translations.component';
import { WorkflowLineageComponent } from './components/workflow-lineage/workflow-lineage.component';
import { WorkflowComponent } from './components/workflow/workflow.component';
import { SharedEffects } from './core/shared.effects';
import { reducer } from './core/shared.reducer';
import { MapPopupWrapperComponent } from './components/map-popup/map-popup-wrapper/map-popup-wrapper.component';
import { CurrencyInputDirective } from './directives/currency-input.directive';
import { OnlyDigitsDirective } from './directives/only-digits.directive';
import { PhoneNumberDirective } from './directives/phone-number.directive';
import { PreventInputDirective } from './directives/prevent-input.directive';
import { MapPopupTypeOneComponent } from './components/map-popup/components/map-popup-type-one/map-popup-type-one.component';
import {
  MapPopupSummaryTypeOneComponent,
} from './components/map-popup/components/map-popup-summary-type-one/map-popup-summary-type-one.component';
import {
  MapPopupSummaryTypeTwoComponent,
} from './components/map-popup/components/map-popup-summary-type-two/map-popup-summary-type-two.component';
import { MapPopupTypeTwoComponent } from './components/map-popup/components/map-popup-type-two/map-popup-type-two.component';
import { ClaimMapPopupComponent } from './components/map-popup/components/claim-map-popup/claim-map-popup.component';
import { IssueMapPopupComponent } from './components/map-popup/components/issue-map-popup/issue-map-popup.component';
import { ShimmerLoaderComponent } from './components/shimmer-loader/shimmer-loader.component';
import { ImageComponent } from './components/image/image.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ViewFieldComponent } from './components/form-fields/view-field/view-field.component';
import { CarouselTypeTwoComponent } from './components/carousel-type-two/carousel-type-two.component';
import { DummyComponent } from './components/dummy/dummy.component';
import { DropdownComponent } from './components/form-fields/input/dropdown/dropdown.component';
import { PrimaryButtonComponent } from './components/buttons/primary-button/primary-button.component';
import { PreventMultipleClickDirective } from './directives/prevent-multiple-click.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DatePickerComponent } from './components/form-fields/input/date/date-picker/date-picker.component';
import { DateRangePickerComponent } from './components/form-fields/input/date/date-range-picker/date-range-picker.component';
import { MatRippleModule } from '@angular/material/core';
import { DateDirective } from './directives/date.directive';
import { DatePickerFormatPipe } from './pipes/date-picker-format.pipe';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { CheckBoxComponent } from './components/form-fields/input/check-box/check-box.component';
import { SecondaryButtonComponent } from './components/buttons/secondary-button/secondary-button.component';
import { TextAreaComponent } from './components/form-fields/input/text-area/text-area.component';
import { HorizontalDatePickerComponent } from './components/form-fields/input/date/horizontal-date-picker/horizontal-date-picker.component';
import { PaginatorComponent } from './components/paginator/paginator.component';
import {
  CheckBoxListWithFilterComponent,
} from './components/form-fields/input/check-box-list-with-filter/check-box-list-with-filter.component';
import { TextAreaTypeTwoComponent } from './components/form-fields/input/text-area-type-two/text-area-type-two.component';
import { TextInputComponent } from './components/form-fields/input/text-input/text-input.component';
import { NumberInputComponent } from './components/form-fields/input/number-input/number-input.component';
import { AutocompleteComponent } from './components/form-fields/input/autocomplete/autocomplete.component';
import { AlertDialogComponent } from './components/alert-dialog/alert-dialog.component';
import { PicturesSectionComponent } from './components/form-components/pictures-section/pictures-section.component';
import { LocationSectionComponent } from './components/form-components/location-section/location-section.component';
import { ButtonSelectComponent } from './components/button-select/button-select.component';
import { VideoDurationPipe } from './pipes/video-duration.pipe';
import { DatePickerOverlayComponent } from './components/form-fields/input/date/overlay-date-picker/date-picker-overlay.component';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ToggleButtonsComponent } from './components/form-components/toggle-buttons/toggle-buttons.component';
import { TimeSelectComponent } from './components/form-fields/input/time/time-select/time-select.component';
import { TimeInputComponent } from './components/form-fields/input/time/time-input/time-input.component';
import { TimeInputDirective } from './directives/time-input.directive';
import { FilterByTagPipe } from './pipes/filter-by-tag.pipe';
import { MultipleTimeSelectComponent } from './components/form-fields/input/time/multiple-time-select/multiple-time-select.component';
import { PictureHoverPreviewComponent } from './components/picture-hover-preview/picture-hover-preview.component';
import {
  PicturesSectionWithTagsComponent,
} from './components/form-components/pictures-section-with-tags/pictures-section-with-tags.component';
import { CheckboxWithListComponent } from './components/form-fields/input/checkbox-with-list/checkbox-with-list.component';
import { MultiSelectDropdownComponent } from './components/form-fields/input/multi-select-dropdown/multi-select-dropdown.component';
import { MarkupEditorComponent } from './components/markup-editor/markup-editor.component';
import { SheetZoomControlsComponent } from '../modules/projects/pages/projects-details-page/components/tab-screens/drawings/sheet-zoom-controls/sheet-zoom-controls.component';
import { YearGridCalendarComponent } from './components/year-grid-calendar/year-grid-calendar.component';
import { CalendarHolidayTableComponent } from './components/calendar-holiday-table/calendar-holiday-table.component';
import { HolidayDialogComponent } from './components/holiday-dialog/holiday-dialog.component';
import { PictureOverlayComponent } from '../modules/projects/components/picture-overlay/picture-overlay.component';
import { PhotoProofPanelComponent } from './components/photo-proof-panel/photo-proof-panel.component';
// Standalone; the design-context picker reuses the project map's layers / zoom stack (contained variant).
import { MapControlsComponent } from '../modules/projects/components/maps/sites-location-map/map-controls/map-controls.component';
import { MapWidgetsToolbarComponent } from './components/map-widgets-toolbar/map-widgets-toolbar.component';
import { WorkflowDiscussionComponent } from './components/workflow-discussion/workflow-discussion.component';
import { IssueStageChipComponent } from './components/issue-workflow/issue-stage-chip/issue-stage-chip.component';
import {
  IssueWorkflowPanelComponent,
} from './components/issue-workflow/issue-workflow-panel/issue-workflow-panel.component';

@NgModule({ declarations: [
        HeaderComponent,
        FooterComponent,
        HumanReadablePipe,
        HumanReadableSizePipe,
        DateFormatPipe,
        DurationFormatPipe,
        DateDurationFormatPipe,
        FilterPipe,
        FileUploaderComponent,
        ConfirmDialogComponent,
        SearchComponent,
        GoogleMapsComponent,
        SidebarComponent,
        ModalComponent,
        ComponentDirective,
        ModalHostDirective,
        WorkflowComponent,
        WorkflowLineageComponent,
        AppIconComponent,
        LocationPickerComponent,
        DesignContextComponent,
        DesignCapturePreviewComponent,
        DesignContextPickerDialogComponent,
        CarouselComponent,
        ProgressCardComponent,
        ProjectCardComponent,
        SubmittalPackageLogComponent,
        FieldsComponent,
        ReportsComponent,
        FieldInputComponent,
        TranslationsComponent,
        MaterialTestsComponent,
        JobCodesComponent,
        EquipmentTypesComponent,
        EsriMapComponent,
        MapPopupWrapperComponent,
        CurrencyInputDirective,
        OnlyDigitsDirective,
        PreventInputDirective,
        MapPopupTypeOneComponent,
        MapPopupSummaryTypeOneComponent,
        MapPopupSummaryTypeTwoComponent,
        MapPopupTypeTwoComponent,
        ClaimMapPopupComponent,
        IssueMapPopupComponent,
        ImageComponent,
        ShimmerLoaderComponent,
        ViewFieldComponent,
        CarouselTypeTwoComponent,
        LoadingScreenComponent,
        DummyComponent,
        DropdownComponent,
        PrimaryButtonComponent,
        PreventMultipleClickDirective,
        DatePickerComponent,
        DateRangePickerComponent,
        DateDirective,
        DatePickerFormatPipe,
        CheckBoxComponent,
        SecondaryButtonComponent,
        TextAreaComponent,
        HorizontalDatePickerComponent,
        DatePickerOverlayComponent,
        NumberInputComponent,
        AutocompleteComponent,
        PaginatorComponent,
        CheckBoxListWithFilterComponent,
        TextAreaTypeTwoComponent,
        TextInputComponent,
        CheckBoxListWithFilterComponent,
        AlertDialogComponent,
        PicturesSectionComponent,
        LocationSectionComponent,
        ButtonSelectComponent,
        VideoDurationPipe,
        SafePipe,
        ToggleButtonsComponent,
        TimeSelectComponent,
        TimeInputComponent,
        TimeInputDirective,
        FilterByTagPipe,
        PicturesSectionWithTagsComponent,
        SentenceCasePipe,
        PictureHoverPreviewComponent,
        MultipleTimeSelectComponent,
        CheckboxWithListComponent,
        MultiSelectDropdownComponent,
        YearGridCalendarComponent,
        CalendarHolidayTableComponent,
        HolidayDialogComponent,
        PictureOverlayComponent,
        PhotoProofPanelComponent,
        MarkupEditorComponent,
        MapWidgetsToolbarComponent,
        WorkflowDiscussionComponent,
        IssueStageChipComponent,
        IssueWorkflowPanelComponent,
    ],
    exports: [
        HeaderComponent,
        FooterComponent,
        HumanReadablePipe,
        HumanReadableSizePipe,
        DateFormatPipe,
        DurationFormatPipe,
        DateDurationFormatPipe,
        FilterPipe,
        MaterialModule,
        AbilityModule,
        FileUploaderComponent,
        ConfirmDialogComponent,
        SearchComponent,
        GoogleMapsComponent,
        SidebarComponent,
        ModalComponent,
        WorkflowComponent,
        WorkflowLineageComponent,
        AppIconComponent,
        NgxMaterialTimepickerModule,
        LocationPickerComponent,
        DesignContextComponent,
        DesignContextChipsComponent,
        DesignCapturePreviewComponent,
        CarouselComponent,
        ProgressCardComponent,
        ProjectCardComponent,
        SubmittalPackageLogComponent,
        FieldsComponent,
        ReportsComponent,
        FieldInputComponent,
        TranslationsComponent,
        MaterialTestsComponent,
        JobCodesComponent,
        EquipmentTypesComponent,
        EsriMapComponent,
        MapPopupWrapperComponent,
        ClaimMapPopupComponent,
        IssueMapPopupComponent,
        CurrencyInputDirective,
        OnlyDigitsDirective,
        PreventInputDirective,
        ImageComponent,
        ViewFieldComponent,
        CarouselTypeTwoComponent,
        LoadingScreenComponent,
        DropdownComponent,
        PrimaryButtonComponent,
        DatePickerComponent,
        DateRangePickerComponent,
        CheckBoxComponent,
        SecondaryButtonComponent,
        TextAreaComponent,
        DatePickerOverlayComponent,
        HorizontalDatePickerComponent,
        NumberInputComponent,
        AutocompleteComponent,
        PaginatorComponent,
        CheckBoxListWithFilterComponent,
        TextAreaTypeTwoComponent,
        TextInputComponent,
        CheckBoxListWithFilterComponent,
        PicturesSectionComponent,
        LocationSectionComponent,
        ButtonSelectComponent,
        ToggleButtonsComponent,
        TimeSelectComponent,
        FilterByTagPipe,
        PicturesSectionWithTagsComponent,
        SentenceCasePipe,
        PictureHoverPreviewComponent,
        MultipleTimeSelectComponent,
        CheckboxWithListComponent,
        MultiSelectDropdownComponent,
        YearGridCalendarComponent,
        CalendarHolidayTableComponent,
        PictureOverlayComponent,
        PhotoProofPanelComponent,
        MatTooltipModule,
        PhoneNumberDirective,
        MarkupEditorComponent,
        MapWidgetsToolbarComponent,
        WorkflowDiscussionComponent,
        IssueStageChipComponent,
        IssueWorkflowPanelComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [CommonModule, DesignContextChipsComponent,
        DesignContextSheetPickerComponent,
        SheetZoomControlsComponent,
        RouterModule,
        MaterialModule,
        AbilityModule,
        GoogleMapsModule,
        ReactiveFormsModule,
        FormsModule,
        NgxMaterialTimepickerModule,
        NgxSkeletonLoaderModule,
        StoreModule.forFeature('sitenotes.shared', reducer),
        EffectsModule.forFeature([SharedEffects]),
        MatTooltipModule,
        MatSnackBarModule,
        MatRippleModule,
        CdkConnectedOverlay,
        CdkOverlayOrigin,
        PhoneNumberDirective,
        MapControlsComponent], providers: [
        DatePipe,
        DatePickerFormatPipe,
        // LocationPickerLauncherService opens it through this token (keeps @arcgis/core out of the launcher's imports).
        { provide: DESIGN_CONTEXT_PICKER_DIALOG, useValue: DesignContextPickerDialogComponent },
    ] })
export class SharedModule {
}
